import { FormEvent, useEffect, useRef, useState } from 'react';
import Avatar from '@mui/material/Avatar';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import Fab from '@mui/material/Fab';
import IconButton from '@mui/material/IconButton';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import Close from '@mui/icons-material/Close';
import Send from '@mui/icons-material/Send';
import MailOutline from '@mui/icons-material/MailOutline';
import { useTranslation } from 'react-i18next';
import axios from '../../api/axios';
import { colors } from '../../styles/colors';
import { Z } from '../../styles/zIndex';
import { supportCopy, supportLocale } from './copy';

type Message = { role: 'user' | 'assistant'; content: string };
const avatar = `${process.env.PUBLIC_URL || ''}/images/nektia-ai-assistant.png`;

/** Adapted from the marketing site's AskNektiaChat. Never calls the fleet agent. */
export function NektiaSupport() {
  const { i18n } = useTranslation();
  const locale = supportLocale(i18n.resolvedLanguage || i18n.language || 'en');
  const copy = supportCopy[locale];
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState(false);
  const pending = useRef<AbortController | null>(null);
  const log = useRef<HTMLDivElement>(null);

  useEffect(() => () => { pending.current?.abort(); }, []);
  useEffect(() => {
    if (open && log.current) log.current.scrollTop = log.current.scrollHeight;
  }, [open, messages, sending, error]);

  // No token, URL, vehicle ID, account information or transcript is included.
  const supportEmail = `mailto:contact@codenekt.com?subject=${encodeURIComponent(copy.subject)}&body=${encodeURIComponent(copy.body)}`;

  async function send(question: string) {
    const text = question.replace(/\s+/g, ' ').trim().slice(0, 700);
    if (!text || pending.current) return;
    const controller = new AbortController();
    pending.current = controller;
    const next: Message[] = [...messages.slice(-7), { role: 'user', content: text }];
    const outgoing = next.map(message => ({ ...message, content: message.content.slice(0, 700) }));
    setMessages(next);
    setInput('');
    setError(false);
    setSending(true);
    try {
      const response = await axios.post<{ answer: string }>('/support/nektia/chat', { messages: outgoing, locale }, {
        signal: controller.signal, timeout: 35000,
      });
      if (typeof response.data.answer !== 'string' || !response.data.answer.trim()) throw new Error('Empty answer');
      if (!controller.signal.aborted) {
        setMessages([...next, { role: 'assistant', content: response.data.answer }].slice(-16) as Message[]);
      }
    } catch {
      if (!controller.signal.aborted) {
        setMessages(messages);
        setInput(text);
        setError(true);
      }
    } finally {
      if (!controller.signal.aborted) setSending(false);
      if (pending.current === controller) pending.current = null;
    }
  }

  if (!localStorage.getItem('token')) return null;
  const face = <Avatar src={avatar} alt="" sx={{ width: 34, height: 34, bgcolor: '#152343', '& img': { transform: 'scale(2.2)', objectPosition: 'top' } }} />;

  return <>
    <Fab variant="extended" onClick={() => setOpen(true)} aria-label={copy.open} aria-haspopup="dialog" aria-expanded={open}
      sx={{ position: 'fixed', right: { xs: 16, sm: 24 }, bottom: 'max(20px, env(safe-area-inset-bottom))', zIndex: Z.SIDEBAR_TRIGGER,
        bgcolor: '#2563eb', color: '#fff', gap: 1, minWidth: 56, px: { xs: 1.3, sm: 2 }, '&:hover': { bgcolor: '#1d4ed8' } }}>
      {face}<Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>{copy.open}</Box>
    </Fab>
    <Dialog open={open} onClose={() => setOpen(false)} aria-labelledby="nektia-support-title" maxWidth={false}
      sx={{ '& .MuiDialog-container': { alignItems: 'flex-end', justifyContent: 'flex-end' } }}
      PaperProps={{ sx: { width: { xs: 'calc(100% - 24px)', sm: 400 }, maxWidth: 400, height: 600,
        maxHeight: 'calc(100dvh - 32px)', m: { xs: 1.5, sm: 3 }, borderRadius: 3, bgcolor: colors.surface, color: colors.textPrimary,
        border: `1px solid ${colors.borderLight}`, overflow: 'hidden' } }}>
      <Box sx={{ p: 2, display: 'flex', alignItems: 'center', gap: 1.5, borderBottom: `1px solid ${colors.borderLight}` }}>
        {face}<Box sx={{ flex: 1 }}><Typography id="nektia-support-title" fontWeight={700}>{copy.title}</Typography>
          <Typography variant="caption" color="text.secondary">{copy.subtitle}</Typography></Box>
        <IconButton onClick={() => setOpen(false)} aria-label={copy.close}><Close /></IconButton>
      </Box>
      <Box ref={log} role="log" aria-live="polite" aria-label={copy.title} sx={{ flex: 1, minHeight: 0, overflowY: 'auto', p: 2, overscrollBehavior: 'contain' }}>
        <Typography sx={{ fontSize: 14, lineHeight: 1.7, mb: 2 }}>{copy.welcome}</Typography>
        {messages.length === 0 ? <Box sx={{ display: 'grid', gap: 1, mb: 2 }}>{copy.suggestions.map(question =>
          <Button key={question} variant="outlined" disabled={sending} onClick={() => send(question)} sx={{ textTransform: 'none', justifyContent: 'flex-start', textAlign: 'left' }}>{question}</Button>)}</Box> : null}
        {messages.map((message, index) => <Box key={index} sx={{ display: 'flex', justifyContent: message.role === 'user' ? 'flex-end' : 'flex-start', mb: 1.5 }}>
          <Typography sx={{ maxWidth: '90%', p: 1.5, borderRadius: 2, fontSize: 14, lineHeight: 1.65, whiteSpace: 'pre-wrap', overflowWrap: 'anywhere',
            bgcolor: message.role === 'user' ? '#2563eb' : colors.elevatedSurface, color: message.role === 'user' ? '#fff' : colors.textPrimary }}>{message.content}</Typography>
        </Box>)}
        {sending ? <Box role="status" sx={{ display: 'flex', gap: 1, alignItems: 'center', fontSize: 14 }}><CircularProgress size={16} />{copy.thinking}</Box> : null}
        {error ? <Typography role="alert" color="error" sx={{ mt: 1, fontSize: 13 }}>{copy.error}</Typography> : null}
      </Box>
      <Box sx={{ p: 1.5, borderTop: `1px solid ${colors.borderLight}` }}>
        <Box component="form" onSubmit={(event: FormEvent) => { event.preventDefault(); void send(input); }} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <TextField autoFocus fullWidth size="small" value={input} disabled={sending} placeholder={copy.question}
            inputProps={{ maxLength: 700, 'aria-label': copy.question }} onChange={event => setInput(event.target.value)} />
          <IconButton type="submit" color="primary" disabled={!input.trim() || sending} aria-label={copy.send}><Send /></IconButton>
        </Box>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>{copy.privacy}</Typography>
        <Button component="a" href={supportEmail} startIcon={<MailOutline />} fullWidth sx={{ textTransform: 'none', mt: 1 }}>{copy.contact}</Button>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textAlign: 'center' }}>{copy.emailNote}</Typography>
      </Box>
    </Dialog>
  </>;
}
